import { CardModel } from "src/swagger/api.client.generated";

export enum CardTypeEnum {
  Activity = 0,
  Activity_Recommended = 1,
  Angel_Aqua = 2,
  Angel_Green = 3,
  Angel_Navy = 4,
  Angel_Purple = 5,
  Angel_Pink = 6,
  Code = 7,
  AllAngel = 8,
  AllActivity = 9,
  MyCard = 10,
  Favorite = 11,
  AllCards = 12,
  AllMyCards = 13,
}

export interface UserAttribute {
  isUserDeck: boolean;
  isFavourite: boolean;
  cardId: number;
  delete?: boolean;
}

export class ControlOptions {
  // Show the control icons (hamburger menu, check icon, expand icon, flip icon)
  showControl: boolean = false;
  // Show the favorite icon
  isFavorite: boolean = false;
  // Show the my card icon
  isMyCard: boolean = false;
  // Show the hamburger menu icon
  showHamburgerMenu: boolean = false;
  // Show the check icon - completed card
  showCheckIcon: boolean = false;
  // Show the expand icon - expand the card
  showExpand?: boolean = true;
  // Show the flip icon - flip the card
  showFlipIcon?: boolean = false;
  // Show the swap action in the hamburger menu
  showSwap?: boolean = true;
  // Picker-only: hide the DELETE menu item for mandatory activity cards (they can't be
  // soft-deleted from the library). Left unset elsewhere so DELETE shows as normal.
  hideDeleteWhenMandatory?: boolean = false;
  // When true, the action menu's destructive item is REMOVE (takes the card out of the
  // current plan; emits the "remove" action) instead of DELETE. Set in plan-edit and
  // Sanctuary; left unset in the cardpicker, where the item is a deck-level delete.
  removeFromPlan?: boolean = false;
  // Onboarding coach-mark anchors for the three-dot menu on every card that shares these options -
  // the trigger (CA01 / DM04) and the opened menu (CA02), FLIP-386. The lessons pick the first
  // visible one, so leaving these unset (everywhere but Plan edit and the Deck Manager) is a no-op.
  menuTriggerAnchor?: string;
  menuRowAnchorPrefix?: string;
  // Saved-Plan walkthrough anchors, FLIP-386 (UP02 an Activity Card, UP04 a card's expand icon, UP05
  // the flip icon on the expanded card). Like the menu anchors, several cards can share one key.
  activityCardAnchor?: string;
  expandAnchor?: string;
  flipAnchor?: string;
}

export enum BackgroundStyle {
  Angel = "background-angel",
  Activity = "background-activity",
  ActivityRecommended = "background-red",
  Transparent = "background-transparent",
  Custom = "background-custom",
  None = "background-none",
}

export enum BorderStyle {
  Aqua = "border-aqua",
  Green = "border-green",
  Navy = "border-navy",
  Pink = "border-pink",
  Purple = "border-purple",
  Yellow = "border-yellow",
  Red = "border-red",
  Orange = "border-orange",
  Dotted = "dotted",
  User = "user",
  None = "none",
}

export enum FaceStyle {
  None = "face-none",
  Aqua = "face-aqua",
  Green = "face-green",
  Navy = "face-navy",
  Pink = "face-pink",
  Purple = "face-purple",
  Orange = "face-orange",
}

export enum CardCategory {
  None = "category-none", // No color
  Body = "body", // var(--card-body) #00C65F
  Experiences = "experiences", // var(--card-experiences) #D1B300
  Feelings = "feelings", // var(--card-feelings) #ed87d1
  Novelty_And_Play = "noveltyplay", // var(--card-novelty-play) #A66CE9
  Relationships = "relationships", // var(--card-relationships) #FF8E00
  Thoughts = "thoughts", // var(--card-thoughts) #0073CF
  Anchor = "anchor", // var(--card-anchor) #e43925
  Activity = "activity", // var(--card-activity) #D1B300
}

// export enum TextColor {
//   WildHare = "text-wild-hare",
//   ForestPath = "text-forestpath",
//   Custom = "",
// }

export class CardSize {
  constructor(size: Size) {
    this.size = size;
  }

  size: Size;
  width?: number;
}

// width size
export enum Size {
  auto = -1,
  xxsml = 3.125, // 50% (60px)
  xsml = 3.75, // 4.375, // 75% (70px)
  sml = 4.375, // 5, // 100% (80px)
  md = 5, // 5.9375, // 125% (95px)
  lrg = 5.9375, // 6.5625, // 150% (105px)
  FullScreen = "fullscreen",
  Custom = "custom",
}

export enum SizeHeight {
  xxsml = 5, // 50% (95px)
  xsml = 5.9375, // 6.875, // 75% (110px)
  sml = 6.875, // 7.8125, // 100% (125px)
  md = 7.8125, // 9.375, // 125% (150px)
  lrg = 9.375, // 10.3125, // 150% (165px)
}

export enum CardMarginSize {
  xxsml = -2.1875, // 50% (-40px)
  xsml = -2.5, // -2.8125, // 75% (-45px)
  sml = -2.8125, // -3.125, // 100% (-50px)
  md = -3.125, // -3.4375, // 125% (-55px)
  lrg = -3.4375, // -3.4375, // 150% (-55px)
}

export type CardAction = "delete" | "remove" | "favorite" | "swap" | "edit" | "create" | "duplicate" | "clicked";

export interface CardActionEvent {
  action: CardAction;
  card: CardModel;
}

// Semantic safety card categories (independent of DB colour names)
export enum SafetyCardCategory {
  Body             = "body",
  Experiences      = "experiences",
  Feelings         = "feelings",
  Novelty_And_Play = "noveltyplay",
  Relationships    = "relationships",
  Thoughts         = "thoughts",
}

// Discriminated union: the adapter's output type.
// Components are built against CardVariant — not raw CardModel.
// Only resolveCardVariant() in card-type.utils.ts changes when the DB schema changes.
export type CardVariant =
  | { kind: "safety";   category: SafetyCardCategory | null; isCustom: boolean }
  // category: null → Deck 3 card with no known category (legacy Grey)
  | { kind: "activity"; isMandatory: boolean; isCustom: boolean }
  // isMandatory mirrors the future IsMandatory DB column on dbo.Cards
  | { kind: "placeholder" } // empty slot — dashed outline
  | { kind: "empty" };      // invisible slot
